import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  Button,
  useTheme,
  CircularProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { createCheckoutSession, stripePromise } from '../services/stripe';

interface ProductCardProps {
  id: string;
  productId: string;
  name: string;
  price: number;
  description: string;
  image: string;
  images: string[];
}

export const ProductCard: React.FC<ProductCardProps> = ({
  id,
  productId,
  name,
  price,
  description,
  image,
  images = [image],
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const { addToCart } = useCart();

  const handleCardClick = (event: React.MouseEvent) => {
    navigate(`/products/${productId}`);
  };

  const handleAddToCart = (event: React.MouseEvent) => {
    event.stopPropagation();
    addToCart({
      id,
      name,
      price,
      image: images[0],
      quantity: 1,
    });
  };

  const handleBuyNow = async (event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      setLoading(true);
      const sessionId = await createCheckoutSession(id);
      const stripe = await stripePromise;
      if (stripe) {
        const { error } = await stripe.redirectToCheckout({ sessionId });
        if (error) {
          throw error;
        }
      }
    } catch (err) {
      console.error('Error during checkout:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card
      onClick={handleCardClick}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-12px) scale(1.02)',
          boxShadow: theme.shadows[10],
        },
        position: 'relative',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ position: 'relative', paddingTop: '100%', overflow: 'hidden' }}>
        <CardMedia
          component="img"
          image={image}
          alt={name}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transition: 'transform 0.3s ease',
            '&:hover': {
              transform: 'scale(1.1)',
            },
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
            p: 2,
            transition: 'opacity 0.3s ease',
            opacity: 0,
            '&:hover': {
              opacity: 1,
            },
          }}
        >
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
            Click to View Details
          </Typography>
        </Box>
      </Box>

      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', p: 3 }}>
        <Box>
          <Typography
            variant="h6"
            component="h3"
            sx={{
              fontWeight: 600,
              mb: 1,
              fontSize: '1.25rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {name}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {description}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: theme.palette.primary.main,
              mb: 2,
            }}
          >
            ${price.toFixed(2)}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mt: 'auto' }}>
          <Button
            onClick={handleAddToCart}
            variant="outlined"
            sx={{
              flex: 1,
              borderRadius: '25px',
              borderWidth: 2,
              py: 1,
              '&:hover': {
                borderWidth: 2,
                backgroundColor: theme.palette.primary.main,
                color: 'white',
              },
            }}
          >
            Add to Cart
          </Button>
          <Button
            onClick={handleBuyNow}
            variant="contained"
            disabled={loading}
            sx={{
              flex: 1,
              borderRadius: '25px',
              py: 1,
              backgroundColor: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Buy Now'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};
